import * as React from "react";
import { ExportReportPdfButton } from "@/components/ExportReportPdfButton";
import { useNavigate } from "react-router-dom";
import { useFinance } from "@/context/FinanceContext";
import { Header } from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateInput } from "@/components/ui/date-input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Download, FileSpreadsheet, FileText, TrendingUp, TrendingDown, Landmark, DollarSign, ExternalLink, X, Search } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot, Label as RechartsLabel } from "recharts";
import { startOfMonth, endOfMonth, eachDayOfInterval, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { currencyFormatterFull } from "@/lib/formatters";
import { useToast } from "@/components/ui/use-toast";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface DailyRow {
  date: string; // YYYY-MM-DD
  label: string; // dd/MM/yyyy
  openingBalance: number;
  income: number;
  expense: number;
  closingBalance: number;
}

export default function SaldosBancarios() {
  const { transactions, accounts, categories, suppliers } = useFinance();
  const { toast } = useToast();
  const navigate = useNavigate();

  const now = new Date();
  const [startDate, setStartDate] = React.useState<string>(format(startOfMonth(now), "yyyy-MM-dd"));
  const [endDate, setEndDate] = React.useState<string>(format(endOfMonth(now), "yyyy-MM-dd"));
  const [selectedAccountId, setSelectedAccountId] = React.useState<string>("__all__");
  const [drillTypeFilter, setDrillTypeFilter] = React.useState<"all" | "receita" | "despesa">("all");
  const [drillSearch, setDrillSearch] = React.useState<string>("");
  const [drillDay, setDrillDay] = React.useState<string | null>(null);

  React.useEffect(() => {
    document.title = "Saldos Bancários | Radar Financeiro";
  }, []);

  const isAllAccounts = selectedAccountId === "__all__";

  const selectedAccount = React.useMemo(
    () => isAllAccounts ? null : accounts.find(a => a.id === selectedAccountId),
    [accounts, selectedAccountId, isAllAccounts]
  );

  const dailyRows = React.useMemo<DailyRow[]>(() => {
    if ((!isAllAccounts && !selectedAccount) || !startDate || !endDate) return [];
    if (isAllAccounts && accounts.length === 0) return [];

    const [sy, sm, sd] = startDate.split("-").map(Number);
    const [ey, em, ed] = endDate.split("-").map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    if (start > end) return [];

    const days = eachDayOfInterval({ start, end });

    // Filter settled transactions for selected account(s)
    const acctTxns = isAllAccounts
      ? transactions.filter(t => t.settled)
      : transactions.filter(t => t.accountId === selectedAccountId && t.settled);

    // Helper: get YYYY-MM-DD from transaction effective date
    const getEffDate = (t: typeof acctTxns[0]): string | null => t.paymentDate || t.dueDate || null;

    // Calculate opening balance
    let openingBalance = isAllAccounts
      ? accounts.reduce((sum, a) => sum + (a.initialBalance || 0), 0)
      : (selectedAccount?.initialBalance || 0);

    acctTxns.forEach(t => {
      const effDate = getEffDate(t);
      if (!effDate) {
        openingBalance += t.type === "receita" ? t.value : -t.value;
        return;
      }
      const [y, m, d] = effDate.split("-").map(Number);
      const txDate = new Date(y, m - 1, d);
      if (txDate < start) {
        openingBalance += t.type === "receita" ? t.value : -t.value;
      }
    });

    // Build daily map
    const dayMap = new Map<string, { income: number; expense: number }>();
    acctTxns.forEach(t => {
      const effDate = getEffDate(t);
      if (!effDate) return;
      const key = effDate;
      const [y, m, d] = key.split("-").map(Number);
      const txDate = new Date(y, m - 1, d);
      if (txDate < start || txDate > end) return;
      const entry = dayMap.get(key) || { income: 0, expense: 0 };
      if (t.type === "receita") entry.income += t.value;
      else entry.expense += t.value;
      dayMap.set(key, entry);
    });

    let runningBalance = openingBalance;
    return days.map(day => {
      const key = format(day, "yyyy-MM-dd");
      const label = format(day, "dd/MM/yyyy");
      const mov = dayMap.get(key) || { income: 0, expense: 0 };
      const dayOpening = runningBalance;
      const dayClosing = dayOpening + mov.income - mov.expense;
      runningBalance = dayClosing;
      return { date: key, label, openingBalance: dayOpening, income: mov.income, expense: mov.expense, closingBalance: dayClosing };
    });
  }, [selectedAccount, selectedAccountId, isAllAccounts, accounts, transactions, startDate, endDate]);

  // Summary totals
  const totals = React.useMemo(() => {
    if (dailyRows.length === 0) return { opening: 0, income: 0, expense: 0, closing: 0 };
    return {
      opening: dailyRows[0].openingBalance,
      income: dailyRows.reduce((s, r) => s + r.income, 0),
      expense: dailyRows.reduce((s, r) => s + r.expense, 0),
      closing: dailyRows[dailyRows.length - 1].closingBalance,
    };
  }, [dailyRows]);

  // Maps auxiliares para drill-down
  const accountById = React.useMemo(() => new Map(accounts.map(a => [a.id, a])), [accounts]);
  const categoryById = React.useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);
  const supplierById = React.useMemo(() => new Map(suppliers.map(s => [s.id, s])), [suppliers]);

  // Lançamentos que compõem o relatório (mesma regra do dailyRows: settled, conta, período por payment_date||due_date, sem transferências)
  const drillTransactions = React.useMemo(() => {
    if (!startDate || !endDate) return [];
    const [sy, sm, sd] = startDate.split("-").map(Number);
    const [ey, em, ed] = endDate.split("-").map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    if (start > end) return [];

    const base = isAllAccounts
      ? transactions
      : transactions.filter(t => t.accountId === selectedAccountId);

    return base
      .filter(t => t.settled && t.kind !== "transferencia")
      .filter(t => {
        const eff = t.paymentDate || t.dueDate;
        if (!eff) return false;
        const [y, m, d] = eff.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        return dt >= start && dt <= end;
      })
      .filter(t => {
        if (drillDay) {
          const eff = t.paymentDate || t.dueDate;
          return eff === drillDay;
        }
        return true;
      })
      .filter(t => drillTypeFilter === "all" ? true : t.type === drillTypeFilter)
      .filter(t => {
        if (!drillSearch.trim()) return true;
        const q = drillSearch.trim().toLowerCase();
        return (t.description || "").toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const da = a.paymentDate || a.dueDate || "";
        const db = b.paymentDate || b.dueDate || "";
        return db.localeCompare(da);
      });
  }, [transactions, isAllAccounts, selectedAccountId, startDate, endDate, drillDay, drillTypeFilter, drillSearch]);

  const openInLancamentos = (day?: string) => {
    const params = new URLSearchParams();
    const acct = !isAllAccounts ? accountById.get(selectedAccountId) : null;
    if (acct) params.set("accountName", acct.name);
    params.set("paymentDateStart", day || startDate);
    params.set("paymentDateEnd", day || endDate);
    navigate(`/lancamentos?${params.toString()}`);
  };


  const handleExportExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Saldos Bancários");

      const headerFill: ExcelJS.Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF475569" } };
      const headerFont: Partial<ExcelJS.Font> = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };

      ws.columns = [
        { header: "Data", key: "date", width: 14 },
        { header: "Saldo Inicial", key: "opening", width: 18 },
        { header: "Entradas", key: "income", width: 16 },
        { header: "Saídas", key: "expense", width: 16 },
        { header: "Saldo Final", key: "closing", width: 18 },
      ];

      ws.getRow(1).eachCell(cell => {
        cell.fill = headerFill;
        cell.font = headerFont;
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      dailyRows.forEach(r => {
        ws.addRow({ date: r.label, opening: r.openingBalance, income: r.income, expense: r.expense, closing: r.closingBalance });
      });


      // Format currency columns
      const currFmt = '#,##0.00';
      for (let i = 2; i <= ws.rowCount; i++) {
        for (let c = 2; c <= 5; c++) {
          const cell = ws.getRow(i).getCell(c);
          cell.numFmt = currFmt;
        }
      }

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `saldos_bancarios_${selectedAccount?.name || "conta"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: "Sucesso", description: "Excel exportado com sucesso!" });
    } catch {
      toast({ title: "Erro", description: "Falha ao exportar Excel.", variant: "destructive" });
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text(`Saldos Bancários - ${selectedAccount?.name || ""}`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Período: ${dailyRows[0]?.label || ""} a ${dailyRows[dailyRows.length - 1]?.label || ""}`, 14, 22);

      autoTable(doc, {
        startY: 28,
        head: [["Data", "Saldo Inicial", "Entradas", "Saídas", "Saldo Final"]],
        body: dailyRows.map(r => [
            r.label,
            currencyFormatterFull.format(r.openingBalance),
            currencyFormatterFull.format(r.income),
            currencyFormatterFull.format(r.expense),
            currencyFormatterFull.format(r.closingBalance),
          ]),
        headStyles: { fillColor: [71, 85, 105] },
        styles: { fontSize: 9 },
        columnStyles: {
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
        },
      });

      doc.save(`saldos_bancarios_${selectedAccount?.name || "conta"}.pdf`);
      toast({ title: "Sucesso", description: "PDF exportado com sucesso!" });
    } catch {
      toast({ title: "Erro", description: "Falha ao exportar PDF.", variant: "destructive" });
    }
  };

  const summaryCards = [
    { label: "Saldo Inicial", value: totals.opening, icon: Landmark, color: "text-blue-500" },
    { label: "Total Entradas", value: totals.income, icon: TrendingUp, color: "text-emerald-500" },
    { label: "Total Saídas", value: totals.expense, icon: TrendingDown, color: "text-red-500" },
    { label: "Saldo Final", value: totals.closing, icon: DollarSign, color: "text-purple-500" },
  ];

  const reportRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="min-h-screen bg-background">
      <div data-pdf-ignore>
        <Header />
      </div>
      <div ref={reportRef} className="page-bleed py-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Saldos Bancários</h1>
            <p className="text-sm text-muted-foreground">Evolução dos saldos por conta no período</p>
          </div>
          <ExportReportPdfButton
            targetRef={reportRef}
            title="Saldos Bancários"
            subtitle={`Período: ${startDate.split("-").reverse().join("/")} até ${endDate.split("-").reverse().join("/")}`}
            filenameSlug="saldos_bancarios"
          />
        </div>
        {/* Filters */}

        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Data Inicial</Label>
            <DateInput value={startDate} onChange={setStartDate} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Data Final</Label>
            <DateInput value={endDate} onChange={setEndDate} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Conta</Label>
            <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="Selecione a conta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todas as contas</SelectItem>
                {accounts.map(acc => (
                  <SelectItem key={acc.id} value={acc.id}>{acc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={dailyRows.length === 0}>
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportExcel}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF}>
                <FileText className="h-4 w-4 mr-2" />
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {summaryCards.map(card => (
            <Card key={card.label}>
              <CardContent className="p-4 flex items-center gap-3">
                <card.icon className={`h-8 w-8 ${card.color}`} />
                <div>
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                  <p className={`text-lg font-bold ${card.value < 0 ? "text-red-600" : ""}`}>
                    {currencyFormatterFull.format(card.value)}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Balance Line Chart */}
        {dailyRows.length > 1 && (
          <Card>
            <CardContent className="p-4">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyRows} margin={{ top: 30, right: 40, left: 20, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    interval={Math.max(0, Math.floor(dailyRows.length / 10))}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
                    width={60}
                  />
                  <Tooltip
                    formatter={(value: number) => [currencyFormatterFull.format(value), "Saldo"]}
                    labelFormatter={(label: string) => `Data: ${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="closingBalance"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2.5}
                    dot={false}
                    name="Saldo"
                  />
                  {/* First point label */}
                  <ReferenceDot
                    x={dailyRows[0].label}
                    y={dailyRows[0].closingBalance}
                    r={6}
                    fill="hsl(var(--primary))"
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    <RechartsLabel
                      value={currencyFormatterFull.format(dailyRows[0].closingBalance)}
                      position="top"
                      offset={12}
                      style={{ fontSize: 13, fontWeight: 700, fill: 'hsl(var(--primary))' }}
                    />
                  </ReferenceDot>
                  {/* Last point label */}
                  <ReferenceDot
                    x={dailyRows[dailyRows.length - 1].label}
                    y={dailyRows[dailyRows.length - 1].closingBalance}
                    r={6}
                    fill="hsl(var(--primary))"
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    <RechartsLabel
                      value={currencyFormatterFull.format(dailyRows[dailyRows.length - 1].closingBalance)}
                      position="top"
                      offset={12}
                      style={{ fontSize: 13, fontWeight: 700, fill: 'hsl(var(--primary))' }}
                    />
                  </ReferenceDot>
                  {/* Min balance point */}
                  {(() => {
                    const minRow = dailyRows.reduce((min, r) => r.closingBalance < min.closingBalance ? r : min, dailyRows[0]);
                    return (
                      <ReferenceDot x={minRow.label} y={minRow.closingBalance} r={6} fill="#ef4444" stroke="#fff" strokeWidth={2}>
                        <RechartsLabel value={currencyFormatterFull.format(minRow.closingBalance)} position="bottom" offset={12} style={{ fontSize: 12, fontWeight: 700, fill: '#ef4444' }} />
                      </ReferenceDot>
                    );
                  })()}
                  {/* Max balance point */}
                  {(() => {
                    const maxRow = dailyRows.reduce((max, r) => r.closingBalance > max.closingBalance ? r : max, dailyRows[0]);
                    return (
                      <ReferenceDot x={maxRow.label} y={maxRow.closingBalance} r={6} fill="#22c55e" stroke="#fff" strokeWidth={2}>
                        <RechartsLabel value={currencyFormatterFull.format(maxRow.closingBalance)} position="top" offset={12} style={{ fontSize: 12, fontWeight: 700, fill: '#22c55e' }} />
                      </ReferenceDot>
                    );
                  })()}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        {dailyRows.length > 0 ? (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-auto max-h-[65vh]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="sticky top-0 bg-muted/95 z-10 text-center">Data</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10 text-center">Saldo Inicial</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10 text-center">Entradas</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10 text-center">Saídas</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10 text-center">Saldo Final</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyRows.map(row => {
                      const isSelected = drillDay === row.date;
                      const hasMov = row.income > 0 || row.expense > 0;
                      return (
                        <TableRow
                          key={row.date}
                          className={`cursor-pointer transition-colors ${isSelected ? "bg-primary/10 hover:bg-primary/15" : "hover:bg-muted/40"} ${!hasMov ? "opacity-80" : ""}`}
                          onClick={() => setDrillDay(isSelected ? null : row.date)}
                          title={hasMov ? "Clique para ver os lançamentos deste dia" : "Sem lançamentos neste dia"}
                        >
                          <TableCell className="text-center font-medium">{row.label}</TableCell>
                          <TableCell className={`text-center ${row.openingBalance < 0 ? "text-red-600" : ""}`}>
                            {currencyFormatterFull.format(row.openingBalance)}
                          </TableCell>
                          <TableCell className="text-center text-emerald-600">
                            {row.income > 0 ? currencyFormatterFull.format(row.income) : "-"}
                          </TableCell>
                          <TableCell className="text-center text-red-600">
                            {row.expense > 0 ? currencyFormatterFull.format(row.expense) : "-"}
                          </TableCell>
                          <TableCell className={`text-center font-semibold ${row.closingBalance < 0 ? "text-red-600" : ""}`}>
                            {currencyFormatterFull.format(row.closingBalance)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              {accounts.length === 0
                ? "Nenhuma conta cadastrada. Cadastre uma conta para ver os saldos."
                : "Selecione uma conta e um período para visualizar os saldos diários."}
            </CardContent>
          </Card>
        )}

        {/* Drill-down: Lançamentos que compõem o relatório */}
        {dailyRows.length > 0 && (
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold">Lançamentos do período</h3>
                  {drillDay && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium bg-primary/10 text-primary px-2 py-1 rounded">
                      Dia {format(new Date(drillDay + "T00:00:00"), "dd/MM/yyyy")}
                      <button
                        type="button"
                        onClick={() => setDrillDay(null)}
                        className="ml-1 hover:text-primary/70"
                        aria-label="Remover filtro do dia"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    ({drillTransactions.length} {drillTransactions.length === 1 ? "lançamento" : "lançamentos"})
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="inline-flex rounded-md border bg-muted/30 p-0.5">
                    <Button
                      variant={drillTypeFilter === "all" ? "default" : "ghost"}
                      size="sm"
                      className="h-7 px-3"
                      onClick={() => setDrillTypeFilter("all")}
                    >
                      Todos
                    </Button>
                    <Button
                      variant={drillTypeFilter === "receita" ? "default" : "ghost"}
                      size="sm"
                      className="h-7 px-3"
                      onClick={() => setDrillTypeFilter("receita")}
                    >
                      Entradas
                    </Button>
                    <Button
                      variant={drillTypeFilter === "despesa" ? "default" : "ghost"}
                      size="sm"
                      className="h-7 px-3"
                      onClick={() => setDrillTypeFilter("despesa")}
                    >
                      Saídas
                    </Button>
                  </div>
                  <div className="relative">
                    <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={drillSearch}
                      onChange={(e) => setDrillSearch(e.target.value)}
                      placeholder="Buscar descrição"
                      className="h-8 pl-7 w-full sm:w-[220px]"
                    />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openInLancamentos()}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    Abrir em Lançamentos
                  </Button>
                </div>
              </div>

              <div className="overflow-auto max-h-[55vh] border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="sticky top-0 bg-muted/95 z-10">Pagamento</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10">Descrição</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10">Categoria</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10">Fornecedor</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10">Conta</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10 text-center">Tipo</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10 text-right">Valor</TableHead>
                      <TableHead className="sticky top-0 bg-muted/95 z-10 w-[60px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {drillTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                          Nenhum lançamento encontrado para os filtros atuais.
                        </TableCell>
                      </TableRow>
                    ) : (
                      drillTransactions.map(t => {
                        const eff = t.paymentDate || t.dueDate || "";
                        const isReceita = t.type === "receita";
                        return (
                          <TableRow key={t.id}>
                            <TableCell className="whitespace-nowrap">
                              {eff ? format(new Date(eff + "T00:00:00"), "dd/MM/yyyy") : "-"}
                            </TableCell>
                            <TableCell className="max-w-[280px] truncate" title={t.description}>
                              {t.description || "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {categoryById.get(t.categoryId || "")?.name || "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {supplierById.get(t.supplierId || "")?.name || "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {accountById.get(t.accountId || "")?.name || "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${isReceita ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                                {isReceita ? "Entrada" : "Saída"}
                              </span>
                            </TableCell>
                            <TableCell className={`text-right font-semibold whitespace-nowrap ${isReceita ? "text-emerald-600" : "text-red-600"}`}>
                              {currencyFormatterFull.format(t.value)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                title="Abrir em Lançamentos"
                                onClick={() => openInLancamentos(eff || undefined)}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
